export const billingConfiguration = {
  header: {
    title: "Rechnungseinstellungen",
    description:
      "Verwalten Sie die Rechnungsaussteller Ihres Kontos und ordnen Sie jedem Inserat den passenden zu.",
  },

  intro: {
    lead:
      "Ein <strong>Rechnungsaussteller</strong> ist die steuerliche Einheit, die Ihren Gästen Rechnungen stellt. Sie können mehrere anlegen und festlegen, welcher für welches Inserat abrechnet.",
    feats: {
      fiscalData: {
        label: "Eigene Steuerdaten",
        tip: "Firmenname, NIF/CIF und Steueranschrift, die auf der Rechnung erscheinen.",
      },
      series: {
        label: "Eigene Serien",
        tip: "Jeder Aussteller führt seine eigene Serie und Nummernfolge (auch für Korrekturrechnungen), niemals vermischt.",
      },
      verifactu: {
        label: "VeriFactu pro Aussteller",
        tip: "Jeder Aussteller erfüllt VeriFactu (Signatur und AEAT-Registrierung) separat, gemäß seiner Steuerkonfiguration.",
      },
      assignment: {
        label: "Zuordnung pro Inserat",
        tip: "Ordnen Sie jedem Inserat einen Aussteller zu. Nicht zugeordnete nutzen den primären Aussteller.",
      },
    },
    foot: "Beim Ausstellen wird der Aussteller des Inserats der Buchung verwendet (beim Check-out oder wenn der Gast die Rechnung beim KI-Agenten anfordert).",
  },

  issuers: {
    card: {
      title: "Rechnungsaussteller",
      subtitle: "Legen Sie mehrere Aussteller an und ordnen Sie ihnen Ihre Inserate zu.",
    },
    searchPlaceholder: "Aussteller nach Name oder NIF suchen…",
    newButton: "Neuer Aussteller",
    noName: "Ohne Namen",
    defaultBadge: "Primär",
    listingsCount: "{count} Inserate",
    empty: {
      noMatch: "Kein Aussteller entspricht Ihrer Suche.",
      none: "Sie haben noch keine Aussteller. Legen Sie den ersten über „Neuer Aussteller“ an.",
    },
  },

  assign: {
    card: {
      title: "Zuordnung der Inserate",
      subtitle:
        "Legen Sie fest, welcher Aussteller für welches Inserat abrechnet. Ein Inserat kann nur einen Aussteller haben; nicht zugeordnete nutzen den primären.",
    },
    searchPlaceholder: "Inserat suchen…",
    filter: {
      all: "Alle",
      unassigned: "Nicht zugeordnet",
    },
    issuerFallback: "Aussteller Nr. {id}",
    principalSuffix: " (primär)",
    unassignedOption: "Nicht zugeordnet (primär)",
    empty: {
      noMatch: "Kein Inserat entspricht dem Filter.",
      none: "Sie haben noch keine Inserate.",
    },
    count: "{shown} von {total} Inseraten",
  },

  drawer: {
    editTitle: "Aussteller bearbeiten",
    newTitle: "Neuer Aussteller",
    subtitle: "Steuerdaten des Rechnungsausstellers",
    closeAria: "Schließen",
    footer: {
      isDefault: "Ist der primäre",
      setDefault: "Als primär festlegen",
      delete: "Löschen",
      cancel: "Abbrechen",
      saving: "Wird gespeichert...",
      save: "Aussteller speichern",
    },
  },

  company: {
    title: "Unternehmensdaten",
    subtitle: "Steuerliche Angaben zu Ihrer Unterkunft",
    country: {
      label: "Rechnungsland",
      hint: "Wählen Sie das Steuerland, das für diese Konfiguration gilt.",
    },
    personType: {
      label: "Art der Einheit",
      company: "Juristische Person (Unternehmen)",
      individual: "Natürliche Person (Einzelunternehmen)",
    },
    legalName: {
      label: "Name / Firmenname",
      placeholderCompany: "z. B. INNDEV S.L.",
      placeholderIndividual: "z. B. María García",
      hint: "Steuerlicher Name des Unternehmens oder der Person",
    },
    co: {
      regime: {
        label: "DIAN-Regime",
        common: "Allgemeines Regime",
        simplified: "Vereinfachtes Regime",
        hint: "Für die elektronische Rechnungsstellung der DIAN erforderlich",
      },
      municipality: {
        label: "DIAN-Gemeinde",
        hint: "DIVIPOLA-Code Ihrer Gemeinde, von der DIAN gefordert.",
        selected: "Ausgewählt: {code}",
      },
      responsibilities: {
        label: "Steuerliche Pflichten",
        placeholder: "z. B. O-13, R-99-PN",
        hint: "Codes durch Kommas getrennt. Am häufigsten: <strong>O-13</strong>",
      },
    },
    mx: {
      regime: {
        label: "Steuerregime (SAT)",
        placeholder: "z. B. 601",
        hint: "Code des SAT-Steuerregimes",
      },
      zip: {
        label: "Steuerliche Postleitzahl (SAT)",
        placeholder: "z. B. 06600",
        hint: "Steuerliche Postleitzahl der SAT",
      },
    },
  },

  address: {
    title: "Steueranschrift",
    subtitle: "Die Anschrift, die auf den Rechnungen erscheint",
    street: {
      label: "Straße und Hausnummer",
    },
    city: {
      label: "Stadt",
      hint: "Stadt und Postleitzahl",
    },
    email: {
      label: "E-Mail für Rechnungen",
      placeholder: "rechnung@ihreunterkunft.com",
    },
    phone: {
      label: "Telefon",
    },
    ph: {
      ES: {
        street: "z. B. Gran Vía 42, 3. Stock",
        city: "Madrid",
        zip: "28001",
        regionLabel: "Provinz",
        region: "Madrid",
        phone: "+34 123 456 789",
      },
      CO: {
        street: "z. B. Calle 13 No. 10-25",
        city: "Bogotá",
        zip: "110111",
        regionLabel: "Departement",
        region: "Cundinamarca",
        phone: "+57 601 234 5678",
      },
      MX: {
        street: "z. B. Av. Insurgentes Sur 1234",
        city: "Mexiko-Stadt",
        zip: "06600",
        regionLabel: "Bundesstaat",
        region: "CDMX",
        phone: "+52 55 1234 5678",
      },
    },
  },

  bank: {
    title: "Konto für Zahlungen",
    subtitle: "Wohin überwiesen wird, wenn per Überweisung bezahlt wird",
    iban: {
      label: "Kontonummer (IBAN)",
      placeholder: "DE89 3704 0044 0532 0130 00",
      hint: "Erscheint nur auf der Rechnung, wenn die Zahlungsart Überweisung ist.",
      error: "Die Kontonummer (IBAN) ist nicht gültig.",
    },
    bic: {
      label: "BIC / SWIFT",
      placeholder: "COBADEFFXXX",
      hint: "Optional. Für Zahlungen von außerhalb des SEPA-Raums erforderlich.",
    },
    holder: {
      label: "Kontoinhaber",
      placeholder: "Leer lassen, wenn identisch mit dem Firmennamen",
      hint: "Nur wenn das Konto auf einen anderen Namen läuft als die Firma.",
    },
  },

  invoice: {
    title: "Rechnungseinstellungen",
    subtitle: "Format, Währung und Nummerierung Ihrer Dokumente",
    currency: {
      label: "Standardwährung",
      hint: "Währung, in der die Rechnungen ausgestellt werden",
    },
    taxRate: {
      label: "Standard-MwSt. in %",
      hintCO: "Allgemeine MwSt. Kolumbien: <strong>19 %</strong>",
      hintMX: "Allgemeine MwSt. Mexiko: <strong>16 %</strong>",
      hintES: "Ermäßigte MwSt. für Beherbergung in Spanien: <strong>10 %</strong>, allgemein: <strong>21 %</strong>",
    },
    type: {
      label: "Rechnungsart",
      detailed: {
        label: "Detailliert",
        desc: "Jede Leistung in einer eigenen Zeile",
      },
      simplified: {
        label: "Vereinfacht",
        desc: "Beleg oder Rechnung mit zusammengefassten Positionen",
      },
    },
    numbering: {
      label: "Dokumentnummerierung",
      example: "z. B. {value}",
      invoices: {
        title: "Rechnungen",
      },
      series: "Serie",
      optional: "(optional)",
      optionalPlaceholder: "Optional",
      digits: "Stellen",
      nextNumber: "Nächste Nummer",
      hintOptional: "Die Serie ist optional. Bleibt sie leer, wird die Rechnung nur mit der laufenden Nummer ausgestellt.",
      hintFormat: "Wenn Sie eine Serie festlegen, bildet das System den Code als SERIE-NUMMER. Ohne Serie entsteht nur die NUMMER.",
      hintWarn: "Legen Sie diesen Wert fest, bevor Sie die erste Rechnung der Serie ausstellen, damit die steuerliche Nummernfolge stimmt.",
      hintSandboxCO: "Verwenden Sie in der Sandbox <strong>SETP</strong>, in der Produktion muss es Ihrem DIAN-Beschluss entsprechen.",
    },
    creditNotes: {
      title: "Korrekturrechnungen",
      badge: "Stornos / Korrekturen",
      hint: "Wird für die nächste Korrekturrechnung oder Stornierung verwendet, die Sie ausstellen.",
    },
    footer: {
      label: "Standard-Fußzeile",
      hint: "Text, der am Ende aller Rechnungen erscheint",
      phES: "z. B. Vorgang unterliegt der ermäßigten MwSt. für Beherbergung gemäß Art. 91 des spanischen MwSt.-Gesetzes",
      phCO: "z. B. DIAN-Beschluss Nr. 000123 vom 01.01.2025. Nummernbereiche 1 bis 5000000",
      phMX: "z. B. CFDI ausgestellt gemäß den Anforderungen von Artikel 29-A des CFF",
    },
  },

  advanced: {
    title: "Erweiterte Optionen",
    subtitle: "Zusätzliche Zustelleinstellungen",
    attachPdf: {
      label: "PDF anhängen",
      text: "Das PDF-Dokument zusätzlich zum Download-Link mitsenden",
    },
  },

  loading: {
    default: "Wird geladen...",
    config: "Konfiguration wird geladen...",
    profiles: "Aussteller werden geladen...",
    saving: "Konfiguration wird gespeichert...",
  },

  countries: {
    ES: "Spanien",
    CO: "Kolumbien",
    MX: "Mexiko",
  },

  currencies: {
    EUR: "Euro",
    COP: "Kolumbianischer Peso",
    USD: "US-Dollar",
    MXN: "Mexikanischer Peso",
  },

  taxId: {
    labelFallback: "Steuernummer",
    helpES: "NIF für Privatpersonen, CIF für Unternehmen. z. B. B12345678",
    helpCO: "NIT mit Prüfziffer. z. B. 9014514812 (10 Stellen)",
    helpMX: "RFC mit 12 oder 13 Zeichen. z. B. XAXX010101000",
    helpFallback: "Steuerliche Identifikationsnummer",
  },

  validation: {
    taxIdES: "Ungültiges Format. z. B. B12345678",
    taxIdCO: "Die NIT muss 9 oder 10 Stellen haben. z. B. 9014514812",
    taxIdMX: "Ungültiges RFC-Format. z. B. XAXX010101000",
  },

  msg: {
    reassignError: "Das Inserat konnte nicht neu zugeordnet werden.",
    defaultUpdated: "Primärer Aussteller aktualisiert.",
    setDefaultError: "Festlegen als primär nicht möglich.",
    deleteConfirm: "Diesen Rechnungsaussteller löschen?",
    deleted: "Aussteller gelöscht.",
    deleteError: "Der Aussteller konnte nicht gelöscht werden.",
    requiredFields: "Füllen Sie vor dem Speichern alle Pflichtfelder aus.",
    fixTaxId: "Korrigieren Sie die Steuernummer vor dem Speichern.",
    coRegimeRequired: "Das DIAN-Regime ist für Kolumbien erforderlich.",
    coMunicipalityRequired: "Die DIAN-Gemeinde ist für Kolumbien erforderlich.",
    savedOk: "Aussteller erfolgreich gespeichert.",
    saveError: "Fehler beim Speichern der Konfiguration.",
  },

  misc: {
    thisIssuer: "diesen Aussteller",
  },
};
